import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableHeader,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Wallet, 
  ArrowUpRight, 
  ArrowDownRight, 
  ArrowRightLeft,
  DollarSign,
  Banknote,
  Building,
  RefreshCw,
  Settings,
  Trash2,
  Calendar as CalendarIcon
} from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { format, startOfToday, endOfToday, subDays, startOfMonth, endOfMonth } from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import {
  fetchAccounts,
  fetchAccountSummary,
  fetchAccountTransactions,
  fetchAccountBalanceReport,
  transferBetweenAccounts,
  setAccountOpeningBalance,
  deleteAccountTransaction,
  fetchSettings,
} from '@/api';

interface Account {
  id: string;
  name: string;
  balance: number;
  opening_balance: number;
  description: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface Transaction {
  id: string;
  account_id: string;
  account_name: string;
  transaction_type: 'debit' | 'credit' | 'transfer_in' | 'transfer_out';
  amount: number;
  balance_before: number;
  balance_after: number;
  reference_type: string;
  reference_id: string;
  description: string;
  user_id: string;
  branch_id: string;
  created_at: string;
}

interface BalanceReport {
  current_balances: Array<{
    name: string;
    balance: number;
    opening_balance: number;
    net_change: number;
  }>;
  todays_summary: Array<{
    account_name: string;
    total_credits: number;
    total_debits: number;
    transaction_count: number;
  }>;
  month_summary: Array<{
    account_name: string;
    total_credits: number;
    total_debits: number;
    transaction_count: number;
  }>;
  total_cash_and_bank: number;
}

export default function AccountsPage() {
  useEffect(() => {
    document.title = 'Accounts - Vixalor';
  }, []);
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [accounts, setAccounts] = useState<Account[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [balanceReport, setBalanceReport] = useState<BalanceReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [transferSubmitting, setTransferSubmitting] = useState(false);
  const [openingBalanceDialogOpen, setOpeningBalanceDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState<string | null>(null);
  const [selectedAccount, setSelectedAccount] = useState<string>(() => searchParams.get('account') || 'all');
  const [startDate, setStartDate] = useState<string>(() => searchParams.get('start_date') || '');
  const [endDate, setEndDate] = useState<string>(() => searchParams.get('end_date') || '');
  const [currencySymbol, setCurrencySymbol] = useState<string>('$');
  const [dateRange, setDateRange] = useState<{ from?: Date; to?: Date }>(() => {
    const sd = searchParams.get('start_date');
    const ed = searchParams.get('end_date');
    const from = sd ? new Date(sd) : undefined;
    const to = ed ? new Date(ed) : undefined;
    return { from, to };
  });

  const [pagination, setPagination] = useState({
    page: Math.max(1, parseInt(searchParams.get('page') || '1', 10)),
    per_page: (() => {
      const p = parseInt(searchParams.get('per_page') || '20', 10);
      return [10, 20, 50, 100].includes(p) ? p : 20;
    })(),
    total: 0,
    total_pages: 1,
  });

  // Keep URL in sync with filters and pagination
  useEffect(() => {
    const params: Record<string, string> = {};
    if (selectedAccount && selectedAccount !== 'all') params.account = selectedAccount;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (pagination.page && pagination.page !== 1) params.page = String(pagination.page);
    if (pagination.per_page && pagination.per_page !== 20) params.per_page = String(pagination.per_page);
    setSearchParams(params, { replace: true });
  }, [selectedAccount, startDate, endDate, pagination.page, pagination.per_page, setSearchParams]);

  const [transferForm, setTransferForm] = useState({
    from_account: '',
    to_account: '',
    amount: '',
    description: '',
  });

  const [openingBalanceForm, setOpeningBalanceForm] = useState({
    account: '',
    amount: '',
  });

  const loadData = async () => {
    try {
      setLoading(true);
      const [accountsRes, reportRes, transactionsRes, settingsRes] = await Promise.all([
        fetchAccounts().catch(() => ({ data: [] })),
        fetchAccountBalanceReport().catch(() => ({ data: null })),
        fetchAccountTransactions({ 
          account: selectedAccount !== 'all' ? selectedAccount : undefined,
          start_date: startDate || undefined,
          end_date: endDate || undefined,
          page: pagination.page,
          per_page: pagination.per_page,
        }).catch(() => ({ data: [] })),
        fetchSettings().catch(() => ({ data: { currency_symbol: '$' } })),
      ]);

      // Safely extract data with fallbacks
      const accountsData = accountsRes?.data?.data || accountsRes?.data || [];
      const reportData = reportRes?.data?.data || reportRes?.data || null;
      const transactionsData = transactionsRes?.data?.data || transactionsRes?.data || [];

      setAccounts(Array.isArray(accountsData) ? accountsData : []);
      setBalanceReport(reportData);
      setTransactions(Array.isArray(transactionsData) ? transactionsData : []);
      const p = transactionsRes?.data?.pagination;
      if (p) {
        setPagination(prev => ({
          page: p.page ?? prev.page,
          per_page: p.per_page ?? prev.per_page,
          total: p.total ?? prev.total,
          total_pages: p.total_pages ?? prev.total_pages,
        }));
      }
      
      // Set currency symbol from settings
      const currencySymbolFromSettings = settingsRes?.data?.currency_symbol || settingsRes?.data?.data?.currency_symbol;
      if (currencySymbolFromSettings) {
        setCurrencySymbol(currencySymbolFromSettings);
      }
    } catch (error: any) {
      console.error('Account data loading error:', error);
      // Set safe defaults
      setAccounts([]);
      setBalanceReport(null);
      setTransactions([]);
      setCurrencySymbol('$');
      
      toast({ 
        title: 'Error', 
        description: error.message || 'Failed to load account data', 
        variant: 'destructive' 
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [selectedAccount, startDate, endDate, pagination.page, pagination.per_page]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= pagination.total_pages) {
      setPagination(prev => ({ ...prev, page: newPage }));
    }
  };

  const handleTransfer = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!transferForm.from_account || !transferForm.to_account || !transferForm.amount) {
      toast({ 
        title: 'Error', 
        description: 'Please fill all required fields', 
        variant: 'destructive' 
      });
      return;
    }

    if (transferForm.from_account === transferForm.to_account) {
      toast({ 
        title: 'Error', 
        description: 'Cannot transfer to same account', 
        variant: 'destructive' 
      });
      return;
    }

    const amount = parseFloat(transferForm.amount);
    if (amount <= 0) {
      toast({ 
        title: 'Error', 
        description: 'Amount must be greater than 0', 
        variant: 'destructive' 
      });
      return;
    }

    try {
      setTransferSubmitting(true);
      const response = await transferBetweenAccounts(transferForm);
      const payload = response?.data ?? response;

      if (payload?.success) {
        toast({ 
          title: 'Success', 
          description: payload.message || 'Transfer completed successfully' 
        });
        
        setTransferForm({
          from_account: '',
          to_account: '',
          amount: '',
          description: '',
        });
        setTransferDialogOpen(false);
        await loadData(); // Refresh data
      }
    } catch (error: any) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.error || error.message || 'Transfer failed', 
        variant: 'destructive' 
      });
    } finally {
      setTransferSubmitting(false);
    }
  };

  const handleSetOpeningBalance = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!openingBalanceForm.account || !openingBalanceForm.amount) {
      toast({ 
        title: 'Error', 
        description: 'Please select account and enter amount', 
        variant: 'destructive' 
      });
      return;
    }

    const amount = parseFloat(openingBalanceForm.amount);
    if (amount < 0) {
      toast({ 
        title: 'Error', 
        description: 'Amount cannot be negative', 
        variant: 'destructive' 
      });
      return;
    }

    try {
      const response = await setAccountOpeningBalance(openingBalanceForm);
      
      if (response.success) {
        toast({ 
          title: 'Success', 
          description: response.message || 'Opening balance set successfully' 
        });
        
        setOpeningBalanceForm({
          account: '',
          amount: '',
        });
        setOpeningBalanceDialogOpen(false);
        await loadData(); // Refresh data
      }
    } catch (error: any) {
      toast({ 
        title: 'Error', 
        description: error.message || 'Failed to set opening balance', 
        variant: 'destructive' 
      });
    }
  };

  const handleDeleteTransaction = async () => {
    if (!transactionToDelete) return;

    try {
      const response = await deleteAccountTransaction(transactionToDelete);
      
      if (response.data?.success) {
        toast({ 
          title: 'Success', 
          description: 'Transaction deleted successfully' 
        });
        
        setDeleteDialogOpen(false);
        setTransactionToDelete(null);
        await loadData(); // Refresh data
      }
    } catch (error: any) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.error || 'Failed to delete transaction', 
        variant: 'destructive' 
      });
    }
  };

  const openDeleteDialog = (transactionId: string) => {
    setTransactionToDelete(transactionId);
    setDeleteDialogOpen(true);
  };

  const formatCurrency = (amount: number | string | null | undefined) => {
    // Handle null/undefined amounts and convert strings to numbers
    let safeAmount = 0;
    if (typeof amount === 'number') {
      safeAmount = isNaN(amount) ? 0 : amount;
    } else if (typeof amount === 'string') {
      safeAmount = parseFloat(amount) || 0;
    }
    return `${currencySymbol}${safeAmount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
  };

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'credit':
      case 'transfer_in':
        return <ArrowDownRight className="h-4 w-4 text-green-600" />;
      case 'debit':
      case 'transfer_out':
        return <ArrowUpRight className="h-4 w-4 text-red-600" />;
      default:
        return <ArrowRightLeft className="h-4 w-4 text-blue-600" />;
    }
  };

  const getTransactionBadgeVariant = (type: string): "default" | "secondary" | "destructive" | "outline" => {
    switch (type) {
      case 'credit':
      case 'transfer_in':
        return 'default';
      case 'debit':
      case 'transfer_out':
        return 'destructive';
      default:
        return 'secondary';
    }
  };

  const cashAccount = accounts?.find(a => a.name === 'cash');
  const bankAccount = accounts?.find(a => a.name === 'bank');

  if (loading) {
    return (
      <div className="space-y-8 animate-fade-in">
        <div>
          <h1 className="text-3xl font-bold">Account Management</h1>
          <p className="text-muted-foreground">Loading account data...</p>
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          {[1, 2, 3].map(i => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="h-16 bg-gray-200 animate-pulse rounded"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in">
      <div>
        <h1 className="text-3xl font-bold">Account Management</h1>
        <p className="text-muted-foreground">
          Track cash and bank account balances, transfers, and transaction history
        </p>
      </div>

      {/* Account Balance Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Cash Account</CardTitle>
            <Banknote className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(cashAccount?.balance || 0)}
            </div>
            <p className="text-xs text-muted-foreground">
              Opening: {formatCurrency(cashAccount?.opening_balance || 0)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Bank Account</CardTitle>
            <Building className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(bankAccount?.balance || 0)}
            </div>
            <p className="text-xs text-muted-foreground">
              Opening: {formatCurrency(bankAccount?.opening_balance || 0)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Balance</CardTitle>
            <Wallet className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {(() => {
                const cashBalance = parseFloat(cashAccount?.balance?.toString() || '0') || 0;
                const bankBalance = parseFloat(bankAccount?.balance?.toString() || '0') || 0;
                const total = cashBalance + bankBalance;
                return formatCurrency(total);
              })()}
            </div>
            <p className="text-xs text-muted-foreground">
              Cash + Bank combined
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Action Buttons */}
      {isAdmin && (
        <div className="flex gap-2">
          <Button onClick={() => setTransferDialogOpen(true)}>
            <ArrowRightLeft className="h-4 w-4 mr-2" />
            Transfer Between Accounts
          </Button>
          <Button variant="outline" onClick={() => setOpeningBalanceDialogOpen(true)}>
            <Settings className="h-4 w-4 mr-2" />
            Set Opening Balance
          </Button>
          <Button variant="outline" onClick={loadData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      )}

      {/* Tabs for different views */}
      <Tabs defaultValue="transactions" className="space-y-4">
        <TabsList>
          <TabsTrigger value="transactions">Transactions</TabsTrigger>
          <TabsTrigger value="summary">Summary Report</TabsTrigger>
        </TabsList>

        <TabsContent value="transactions" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Transaction History</CardTitle>
                  <CardDescription>All account transactions and movements</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Select value={selectedAccount} onValueChange={(val) => { setSelectedAccount(val); setPagination(prev => ({ ...prev, page: 1 })); }}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Filter by account" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Accounts</SelectItem>
                      <SelectItem value="cash">Cash Only</SelectItem>
                      <SelectItem value="bank">Bank Only</SelectItem>
                    </SelectContent>
                  </Select>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-[260px] justify-start text-left font-normal">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {dateRange?.from ? (
                          dateRange.to ? (
                            `${format(dateRange.from, 'PP')} - ${format(dateRange.to, 'PP')}`
                          ) : (
                            `${format(dateRange.from, 'PP')} - ...`
                          )
                        ) : (
                          <span>Pick a date range</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-3" align="end">
                      <div className="flex items-center gap-2 mb-3">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const fromDate = startOfToday();
                            const toDate = endOfToday();
                            setDateRange({ from: fromDate, to: toDate });
                            setStartDate(format(fromDate, 'yyyy-MM-dd'));
                            setEndDate(format(toDate, 'yyyy-MM-dd'));
                            setPagination(prev => ({ ...prev, page: 1 }));
                          }}
                        >
                          Today
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const toDate = endOfToday();
                            const fromDate = subDays(startOfToday(), 6);
                            setDateRange({ from: fromDate, to: toDate });
                            setStartDate(format(fromDate, 'yyyy-MM-dd'));
                            setEndDate(format(toDate, 'yyyy-MM-dd'));
                            setPagination(prev => ({ ...prev, page: 1 }));
                          }}
                        >
                          Last 7 days
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const fromDate = startOfMonth(new Date());
                            const toDate = endOfMonth(new Date());
                            setDateRange({ from: fromDate, to: toDate });
                            setStartDate(format(fromDate, 'yyyy-MM-dd'));
                            setEndDate(format(toDate, 'yyyy-MM-dd'));
                            setPagination(prev => ({ ...prev, page: 1 }));
                          }}
                        >
                          This month
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setDateRange({});
                            setStartDate('');
                            setEndDate('');
                            setPagination(prev => ({ ...prev, page: 1 }));
                          }}
                        >
                          Clear
                        </Button>
                      </div>
                      <Calendar
                        mode="range"
                        selected={dateRange}
                        onSelect={(range) => {
                          setDateRange(range || {});
                          const from = range?.from ? format(range.from, 'yyyy-MM-dd') : '';
                          const to = range?.to ? format(range.to, 'yyyy-MM-dd') : '';
                          setStartDate(from);
                          setEndDate(to);
                          setPagination(prev => ({ ...prev, page: 1 }));
                        }}
                        numberOfMonths={2}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date & Time</TableHead>
                      <TableHead>Account</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead className="text-right">Balance After</TableHead>
                      {isAdmin && <TableHead className="text-right">Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {transactions?.map((transaction) => (
                      <TableRow key={transaction.id}>
                        <TableCell>
                          {new Date(transaction.created_at).toLocaleString()}
                        </TableCell>
                        <TableCell className="capitalize">
                          {transaction.account_name}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {getTransactionIcon(transaction.transaction_type)}
                            <Badge variant={getTransactionBadgeVariant(transaction.transaction_type)}>
                              {transaction.transaction_type.replace('_', ' ')}
                            </Badge>
                          </div>
                        </TableCell>
                        <TableCell>{transaction.description}</TableCell>
                        <TableCell className="text-right font-medium">
                          <span className={
                            transaction.transaction_type === 'credit' || transaction.transaction_type === 'transfer_in'
                              ? 'text-green-600'
                              : 'text-red-600'
                          }>
                            {transaction.transaction_type === 'credit' || transaction.transaction_type === 'transfer_in' ? '+' : '-'}
                            {formatCurrency(transaction?.amount)}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(transaction?.balance_after)}
                        </TableCell>
                        {isAdmin && (
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openDeleteDialog(transaction.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className="flex items-center justify-between mt-4">
                <div className="text-sm text-muted-foreground">
                  {(() => {
                    const start = (pagination.page - 1) * pagination.per_page + (transactions.length ? 1 : 0);
                    const end = (pagination.page - 1) * pagination.per_page + transactions.length;
                    return `Showing ${start}-${end} of ${pagination.total} transactions`;
                  })()}
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Per page</span>
                    <Select value={String(pagination.per_page)} onValueChange={(val) => setPagination(prev => ({ ...prev, per_page: parseInt(val, 10), page: 1 }))}>
                      <SelectTrigger className="w-[90px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="20">20</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page === pagination.total_pages}
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="summary" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Today's Summary</CardTitle>
                <CardDescription>Transaction summary for today</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {balanceReport?.todays_summary?.map((summary) => (
                    <div key={summary.account_name} className="space-y-2">
                      <div className="font-medium capitalize">{summary.account_name} Account</div>
                      <div className="grid grid-cols-3 gap-2 text-sm">
                        <div>
                          <span className="text-muted-foreground">Credits:</span>
                          <p className="font-medium text-green-600">
                            +{formatCurrency(summary?.total_credits)}
                          </p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Debits:</span>
                          <p className="font-medium text-red-600">
                            -{formatCurrency(summary?.total_debits)}
                          </p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Transactions:</span>
                          <p className="font-medium">{summary?.transaction_count || 0}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>This Month's Summary</CardTitle>
                <CardDescription>Transaction summary for current month</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {balanceReport?.month_summary?.map((summary) => (
                    <div key={summary.account_name} className="space-y-2">
                      <div className="font-medium capitalize">{summary.account_name} Account</div>
                      <div className="grid grid-cols-3 gap-2 text-sm">
                        <div>
                          <span className="text-muted-foreground">Credits:</span>
                          <p className="font-medium text-green-600">
                            +{formatCurrency(summary?.total_credits)}
                          </p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Debits:</span>
                          <p className="font-medium text-red-600">
                            -{formatCurrency(summary?.total_debits)}
                          </p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Transactions:</span>
                          <p className="font-medium">{summary?.transaction_count || 0}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Transfer Dialog */}
      <Dialog open={transferDialogOpen} onOpenChange={setTransferDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Transfer Between Accounts</DialogTitle>
            <DialogDescription>
              Move money between cash and bank accounts
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleTransfer} className="space-y-4">
            <div>
              <label className="text-sm font-medium">From Account</label>
              <Select
                value={transferForm.from_account}
                onValueChange={(value) => setTransferForm({ ...transferForm, from_account: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select source account" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash Account</SelectItem>
                  <SelectItem value="bank">Bank Account</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium">To Account</label>
              <Select
                value={transferForm.to_account}
                onValueChange={(value) => setTransferForm({ ...transferForm, to_account: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select destination account" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash Account</SelectItem>
                  <SelectItem value="bank">Bank Account</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium">Amount</label>
              <Input
                type="number"
                step="0.01"
                value={transferForm.amount}
                onChange={(e) => setTransferForm({ ...transferForm, amount: e.target.value })}
                placeholder="Enter amount to transfer"
                required
              />
            </div>

            <div>
              <label className="text-sm font-medium">Description (Optional)</label>
              <Input
                value={transferForm.description}
                onChange={(e) => setTransferForm({ ...transferForm, description: e.target.value })}
                placeholder="Enter transfer description"
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setTransferDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={transferSubmitting}>
                {transferSubmitting ? 'Transferring...' : 'Transfer'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Opening Balance Dialog */}
      <Dialog open={openingBalanceDialogOpen} onOpenChange={setOpeningBalanceDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set Opening Balance</DialogTitle>
            <DialogDescription>
              Set the initial balance for an account (one-time setup)
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSetOpeningBalance} className="space-y-4">
            <div>
              <label className="text-sm font-medium">Account</label>
              <Select
                value={openingBalanceForm.account}
                onValueChange={(value) => setOpeningBalanceForm({ ...openingBalanceForm, account: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select account" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash Account</SelectItem>
                  <SelectItem value="bank">Bank Account</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium">Opening Balance</label>
              <Input
                type="number"
                step="0.01"
                value={openingBalanceForm.amount}
                onChange={(e) => setOpeningBalanceForm({ ...openingBalanceForm, amount: e.target.value })}
                placeholder="Enter opening balance"
                required
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setOpeningBalanceDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit">
                Set Balance
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Transaction</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this transaction? This action cannot be undone and will automatically recalculate the account balance.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => setDeleteDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button 
              type="button" 
              variant="destructive"
              onClick={handleDeleteTransaction}
            >
              Delete Transaction
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
